import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import type { EmployeeUserResponse, GetApiCompaniesCurrentAccountmanagementEmployeesParams, OrderType } from "@/api/model"
import { getGetApiCompaniesCurrentAccountmanagementEmployeesQueryKey, useDeleteApiCompaniesCurrentEmployeesEmployeeId, useGetApiCompaniesCurrentAccountmanagementEmployees, useGetApiCompaniesCurrentAccountmanagementEmployeesFilter } from "@/api/generated/company-management-user/company-management-user"
import { notify } from "@/lib/notify"
import { TableCard } from "@/components/common/table/TableCard"
import { PaginationFooter } from "@/components/common/table/PaginationFooter"
import { EmployeesDataTable } from "./EmployeesDataTable"
import { ConfirmDialog } from "@/components/common/ConfirmDialog"
import { FilterRow } from "@/components/common/filters/FilterRow"
import { FilterSearch } from "@/components/common/filters/FilterSearch"
import { FilterDropdown } from "@/components/common/filters/FilterDropdown"
import { Building2, Package, ShoppingBag, Users, Zap } from "lucide-react"

const DEFAULT_PAGE_SIZE = 10;
const FILTERS_STORAGE_KEY = "employees_filters";

/** Only the ID filters are persisted (Search/Sort/Pagination deliberately are not). */
type StoredFilters = Pick<
  GetApiCompaniesCurrentAccountmanagementEmployeesParams,
  "GroupIds" | "SubCompanyIds" | "ActionModuleIds" | "ServiceModuleIds" | "ShopModuleIds"
>;

function readStoredFilters(): StoredFilters {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as StoredFilters) : {}
  } catch {
    return {}
  }
}

function writeStoredFilters(params: GetApiCompaniesCurrentAccountmanagementEmployeesParams) {
  const toStore: StoredFilters = {
    GroupIds: params.GroupIds,
    SubCompanyIds: params.SubCompanyIds,
    ActionModuleIds: params.ActionModuleIds,
    ServiceModuleIds: params.ServiceModuleIds,
    ShopModuleIds: params.ShopModuleIds
  }
  localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toStore))
}

/** Keeps only IDs that the API still knows about; empty selection → undefined. */
function cleanIds(ids: string[] | undefined, valid: Set<string | null | undefined>): string[] | undefined {
  const filtered = ids?.filter((id) => valid.has(id))
  return filtered && filtered.length > 0 ? filtered : undefined
}

interface Props {
  onEdit: (employee: EmployeeUserResponse) => void;
  onModules: (employee: EmployeeUserResponse) => void;
}

export function EmployeesTable({ onEdit, onModules }: Props) {
  const { t } = useTranslation(['employees', 'filters', 'common'])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [confirmEmployee, setConfirmEmployee] = useState<EmployeeUserResponse | null>(null)
  // 1) Read filters from localStorage and use them as the initial value
  const [queryParams, setQueryParams] = useState<GetApiCompaniesCurrentAccountmanagementEmployeesParams>(() => ({
    Offset: 0,
    Limit: DEFAULT_PAGE_SIZE,
    ...readStoredFilters(),
  }))

  // 2) Fetch the filter options from the API
  const { data: filterResponse } = useGetApiCompaniesCurrentAccountmanagementEmployeesFilter()
  // 4) getEmployees with the (possibly cleaned) filters
  const { data: employeesResponse, isLoading, isError } = useGetApiCompaniesCurrentAccountmanagementEmployees(queryParams)

  // 3) Once, after the options have loaded: remove IDs that no longer exist
  const hasCleanedRef = useRef(false)
  useEffect(() => {
    if (hasCleanedRef.current || !filterResponse?.data) return
    hasCleanedRef.current = true

    const options = filterResponse.data
    const validIds = {
      group: new Set((options.groups ?? []).map((g) => g.id)),
      subCompany: new Set((options.subCompanies ?? []).map((s) => s.id)),
      actionModule: new Set((options.actionModules ?? []).map((a) => a.id)),
      serviceModule: new Set((options.serviceModules ?? []).map((s) => s.id)),
      shopModule: new Set((options.shopModules ?? []).map((s) => s.id)),
    }

    setQueryParams((prev) => {
      const cleaned: GetApiCompaniesCurrentAccountmanagementEmployeesParams = {
        ...prev,
        GroupIds: cleanIds(prev.GroupIds, validIds.group),
        SubCompanyIds: cleanIds(prev.SubCompanyIds, validIds.subCompany),
        ActionModuleIds: cleanIds(prev.ActionModuleIds, validIds.actionModule),
        ServiceModuleIds: cleanIds(prev.ServiceModuleIds, validIds.serviceModule),
        ShopModuleIds: cleanIds(prev.ShopModuleIds, validIds.shopModule),
        Offset: 0,
      }
      writeStoredFilters(cleaned)
      return cleaned
    })
  }, [filterResponse])

  // Filter change: set state, persist to localStorage and jump back to page 1
  const updateFilters = (patch: Partial<GetApiCompaniesCurrentAccountmanagementEmployeesParams>) => {
    setQueryParams((prev) => {
      const next = { ...prev, ...patch, Offset: 0 }
      writeStoredFilters(next)
      return next
    })
  }

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: getGetApiCompaniesCurrentAccountmanagementEmployeesQueryKey(),
    })
  }

  const { mutate: deleteEmployee, isPending: isDeleting } = useDeleteApiCompaniesCurrentEmployeesEmployeeId({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.deleted'))
        setConfirmEmployee(null)
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('toasts.deleteError')),
    },
  })

  return (
    <>
      <FilterRow>
        <FilterSearch
          searchLabel={t('filters:searchbar.employeesPage')}
          search={queryParams.Search}
          onChange={(search) => setQueryParams(prev => ({ ...prev, Search: search, Offset: 0 }))}
        />
        <FilterDropdown
            icon={Users}
            label={t('filters:dropdown.groups')}
            availableOptions={filterResponse?.data?.groups ?? []}
            selectedOptions={queryParams.GroupIds}
            onSelect={(ids) => updateFilters({ GroupIds: ids })}
            onReset={() => updateFilters({ GroupIds: undefined })}
          />
          <FilterDropdown
            icon={Building2}
            label={t('filters:dropdown.subCompanies')}
            availableOptions={filterResponse?.data?.subCompanies ?? []}
            selectedOptions={queryParams.SubCompanyIds}
            onSelect={(ids) => updateFilters({ SubCompanyIds: ids })}
            onReset={() => updateFilters({ SubCompanyIds: undefined })}
          />
          <FilterDropdown
            icon={Zap}
            label={t('filters:dropdown.actionModules')}
            availableOptions={filterResponse?.data?.actionModules ?? []}
            selectedOptions={queryParams.ActionModuleIds}
            onSelect={(ids) => updateFilters({ ActionModuleIds: ids })}
            onReset={() => updateFilters({ ActionModuleIds: undefined })}
          />
          <FilterDropdown
            icon={Package}
            label={t('filters:dropdown.serviceModules')}
            availableOptions={filterResponse?.data?.serviceModules ?? []}
            selectedOptions={queryParams.ServiceModuleIds}
            onSelect={(ids) => updateFilters({ ServiceModuleIds: ids })}
            onReset={() => updateFilters({ ServiceModuleIds: undefined })}
          />
          <FilterDropdown
            icon={ShoppingBag}
            label={t('filters:dropdown.shopModules')}
            availableOptions={filterResponse?.data?.shopModules ?? []}
            selectedOptions={queryParams.ShopModuleIds}
            onSelect={(ids) => updateFilters({ ShopModuleIds: ids })}
            onReset={() => updateFilters({ ShopModuleIds: undefined })}
          />
      </FilterRow>
      
      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom relative">
          <EmployeesDataTable
            isLoading={isLoading}
            isError={isError}
            employees={employeesResponse?.data?.items}
            currentSort={queryParams.Sort}
            currentOrder={queryParams.Order}
            onSort={(newSort?: string, newOrder?: OrderType) => setQueryParams(prev => ({ ...prev, Sort: newSort, Order: newOrder, Offset: 0 }))}
            onEdit={onEdit}
            onDelete={setConfirmEmployee}
            onModules={onModules}
            onRowClick={(employee) => {
              if (!employee.id) return
              const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ') || t('list.rowNameFallback')
              navigate(`/employees/${employee.id}`, { state: { breadcrumbs: [{ label: name }] } })
            }}
          />
        </div>

        <PaginationFooter
          offset={queryParams.Offset}
          limit={queryParams.Limit}
          totalCount={employeesResponse?.data?.totalCount}
          onOffsetChange={(offset) => setQueryParams(prev => ({ ...prev, Offset: offset }))}
          onPageSizeChange={(pageSize) => setQueryParams(prev => ({ ...prev, Limit: pageSize, Offset: 0}))}
        />
      </TableCard>

      <ConfirmDialog
        open={!!confirmEmployee}
        title={t('list.delete.title')}
        description={t('list.delete.description', { name: [confirmEmployee?.firstName, confirmEmployee?.lastName].filter(Boolean).join(' ') })}
        confirmLabel={t('common:buttons.delete')}
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
        onConfirm={() => confirmEmployee?.id && deleteEmployee({ employeeId: confirmEmployee.id })}
        onClose={() => setConfirmEmployee(null)}
      />
    </>
  )
}